import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { GLOBAL_CONFIG } from "@/global-config";
import { getFirebaseApp, hasFirebaseAnalyticsConfig } from "@/lib/firebase";

type FirebaseAnalyticsModule = typeof import("firebase/analytics");
type FirebaseAnalytics = import("firebase/analytics").Analytics;

let analyticsModulePromise: Promise<FirebaseAnalyticsModule> | null = null;
let analyticsInstancePromise: Promise<FirebaseAnalytics | null> | null = null;

const loadAnalyticsModule = () => {
	analyticsModulePromise ??= import("firebase/analytics");
	return analyticsModulePromise;
};

async function getFirebaseAnalytics() {
	if (!hasFirebaseAnalyticsConfig || typeof window === "undefined") {
		return null;
	}

	analyticsInstancePromise ??= loadAnalyticsModule()
		.then(async ({ getAnalytics, isSupported }) => {
			const supported = await isSupported().catch(() => false);
			if (!supported) {
				return null;
			}

			const app = getFirebaseApp();
			if (!app) {
				return null;
			}

			return getAnalytics(app);
		})
		.catch((error) => {
			console.warn("[firebase-analytics] init failed", error);
			return null;
		});

	return analyticsInstancePromise;
}

async function trackPageView(pagePath: string) {
	const analytics = await getFirebaseAnalytics();
	if (!analytics || typeof window === "undefined") {
		return;
	}

	const { logEvent } = await loadAnalyticsModule();
	logEvent(analytics, "page_view", {
		page_title: document.title,
		page_location: window.location.href,
		page_path: pagePath,
		app_name: GLOBAL_CONFIG.appName,
	});
}

export function FirebaseAnalyticsBridge() {
	const location = useLocation();
	const previousPathRef = useRef<string | null>(null);

	useEffect(() => {
		void getFirebaseAnalytics();
	}, []);

	useEffect(() => {
		const pagePath = `${location.pathname}${location.search}${location.hash}`;
		if (previousPathRef.current === null) {
			previousPathRef.current = pagePath;
			return;
		}

		if (previousPathRef.current === pagePath) {
			return;
		}

		previousPathRef.current = pagePath;
		void trackPageView(pagePath);
	}, [location.hash, location.pathname, location.search]);

	return null;
}
